export const REDIRECT_PARAM = 'redirect';
export const LOGIN_PATH = '/login';

const MAX_REDIRECT_LENGTH = 512;
const SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;
const WHITESPACE_PATTERN = /\s/;
const BLOCKED_PREFIXES = ['/login', '/logout'];

const hasControlCharacter = (value: string): boolean => {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 32 || code === 127) return true;
  }
  return false;
};

const decodeOnce = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return null;
  }
};

const isUnsafeShape = (value: string): boolean => {
  if (!value || value.length > MAX_REDIRECT_LENGTH) return true;
  if (hasControlCharacter(value)) return true;
  if (SCHEME_PATTERN.test(value)) return true;
  if (value.includes('\\')) return true;
  if (!value.startsWith('/')) return true;
  if (value.startsWith('//')) return true;
  return false;
};

const isBlockedPath = (value: string): boolean => {
  const pathname = value.split('#')[0].split('?')[0].toLowerCase();
  return BLOCKED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

export const sanitizeRedirectPath = (raw?: string | null): string | null => {
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (WHITESPACE_PATTERN.test(trimmed)) return null;
  if (isUnsafeShape(trimmed)) return null;

  const decoded = decodeOnce(trimmed);
  if (decoded === null) return null;
  if (isUnsafeShape(decoded)) return null;

  if (isBlockedPath(trimmed) || isBlockedPath(decoded)) return null;

  return decoded;
};

export const readRedirectFromSearch = (search?: string | null): string | null => {
  if (!search) return null;
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    return sanitizeRedirectPath(params.get(REDIRECT_PARAM));
  } catch (e) {
    return null;
  }
};

export const buildLoginPathWithRedirect = (target?: string | null): string => {
  const clean = sanitizeRedirectPath(target);
  if (!clean) return LOGIN_PATH;
  return `${LOGIN_PATH}?${REDIRECT_PARAM}=${encodeURIComponent(clean)}`;
};
